import { useEffect, useState } from 'react';
import { Box, Icon, Link, Popover, Text } from '@nimbus-ds/components';
import { ChevronDownIcon, ChevronUpIcon, LocationIcon } from '@nimbus-ds/icons';
import classNames from 'classnames';
import { LocationResponseDto } from '@tiendanube/common';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';
import useLocations from 'domains/Shipping/Locations/hooks/useLocations';
import { LocationEntry } from './components';
import './LocationSelector.scss';

interface LocationSelectorProps {
  selectedLocationId?: string;
  label?: string;
  placeholder?: string;
  showPrefix?: boolean;
  showAddress?: boolean;
  disabled?: boolean;
  excludeLocationId?: string;
  showWhenJustOneLocation?: boolean;
  onSelectionChange: (locationId: string) => void;
}

function LocationSelector({
  selectedLocationId,
  label = '',
  placeholder = '',
  showPrefix = true,
  showAddress = false,
  disabled = false,
  excludeLocationId,
  showWhenJustOneLocation = false,
  onSelectionChange,
}: Readonly<LocationSelectorProps>): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(false);

  const t = useTranslationCatalog();

  const { locationMain, locationsOthers, fetchLocations } = useLocations();

  useEffect(() => {
    if (!locationMain) {
      fetchLocations();
    }
  }, [fetchLocations, locationMain]);

  const onLocationChange = (location: LocationResponseDto) => {
    onSelectionChange(location.id);
    setIsOpen(false);
  };

  const allLocations = (
    locationMain ? [locationMain, ...locationsOthers] : locationsOthers
  ).filter((location) => location.id !== excludeLocationId);

  if (allLocations.length <= 1 && !showWhenJustOneLocation) return null;

  const selectedLocation = allLocations.find(
    (location) => location.id === selectedLocationId,
  );

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      flexDirection="column"
      gap="2"
      paddingBottom="2"
      minHeight={showAddress ? '88px' : 'auto'}
    >
      {!!label && (
        <Text
          id="location-selector-label"
          fontSize="caption"
          color="neutral-textLow"
        >
          {label}
        </Text>
      )}
      <div
        className={classNames('stratus--location-selector', {
          'stratus--location-selector__disabled': disabled,
        })}
      >
        <Popover
          arrow={false}
          position="bottom-start"
          padding="small"
          visible={isOpen}
          enabledClick={!disabled}
          onVisibility={setIsOpen}
          content={
            <Box display="flex" gap="1" flexDirection="column" width="100%">
              {allLocations.map((location) => (
                <LocationEntry
                  key={location.id}
                  location={location}
                  isSelected={location.id === selectedLocation?.id}
                  onSelectionChange={onLocationChange}
                />
              ))}
            </Box>
          }
        >
          <Link appearance="neutral" textDecoration="none">
            <Box display="flex" gap="2">
              <Icon
                color="neutral-textHigh"
                source={<LocationIcon />}
                cursor="pointer"
              />
              <Box display="flex" gap="1" as="span">
                {selectedLocationId ? (
                  <>
                    {showPrefix && (
                      <Text
                        as="span"
                        fontSize="highlight"
                        lineHeight="highlight"
                        color="neutral-textLow"
                      >
                        {t('inventory.inLocation')}
                      </Text>
                    )}
                    <Text
                      fontSize="highlight"
                      lineHeight="highlight"
                      as="span"
                      fontWeight="medium"
                      color="neutral-textHigh"
                    >
                      {selectedLocation?.name}
                    </Text>
                  </>
                ) : (
                  <Text>{placeholder}</Text>
                )}
              </Box>
              <Icon
                color="neutral-textHigh"
                source={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                cursor="pointer"
              />
            </Box>
          </Link>
        </Popover>
      </div>
      {!!selectedLocationId && showAddress && (
        <Text fontSize="caption" color="neutral-textLow">
          {selectedLocation?.address}
        </Text>
      )}
    </Box>
  );
}

export default LocationSelector;
